import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Swords, ChevronLeft } from 'lucide-react';
import { api, type MatchCardV2Response, type MatchCardStatus, type Me, type Competition } from '../lib/api';
import { cn } from '../lib/cn';
import { clanLabelFrom } from '../lib/displayName';
import { useEffectiveMe, useViewAs } from '../lib/viewAs';
import { isMatchSideCaptain } from '../lib/matchCaptainAccess';
import { VetoBoard } from '../components/VetoBoard';

// ─────────────────────────────────────────────────────────────────────────────
// VetoPage — focused /veto/:matchId page (Lisa, 2026-07-24). A dedicated
// surface for the map veto, replacing "veto buried in the match card" for
// people who only need to ban/pick maps. Mounts the SAME <VetoBoard> the
// match card mounts, fed by the SAME query key/api call — no second source
// of truth. Only adds a focused header (auto-filled teams) + a link back to
// the full match card. Faction (JSOC/GUILD) pick is a LATER thread — this
// page just mounts the board as-is, same as MatchCardLive does today.
// ─────────────────────────────────────────────────────────────────────────────

export function VetoPage() {
  const { matchId: rawMatchId } = useParams();
  const [qs] = useSearchParams();
  const compId = qs.get('compId') || '';
  const matchId = rawMatchId ? decodeURIComponent(rawMatchId) : '';

  // Same query key/call MatchCardLive uses — one cache entry shared between
  // the two pages, so navigating card ↔ veto never refetches needlessly.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
  const { data: allComps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });

  const cardQuery = useQuery({
    queryKey: ['matchcard', matchId, compId],
    queryFn: () => api.matchCardGet(matchId, compId),
    enabled: !!matchId && !!compId,
    refetchInterval: 4000,
  });

  if (!matchId || !compId) {
    return <Shell><EmptyState text="Missing match or competition id." /></Shell>;
  }
  if (cardQuery.isLoading) {
    return <Shell><EmptyState text="Loading veto…" /></Shell>;
  }
  if (cardQuery.isError || !cardQuery.data) {
    return <Shell><EmptyState text="Match not found." kind="error" /></Shell>;
  }

  const comp = (allComps ?? []).find(c => c.id === compId);

  return (
    <Shell matchId={matchId} compId={compId}>
      <VetoPageBody data={cardQuery.data} me={me} matchId={matchId} compId={compId} comp={comp} />
    </Shell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Shell({ children, matchId, compId }: { children: React.ReactNode; matchId?: string; compId?: string }) {
  const backTo = matchId && compId ? `/matchcard/${encodeURIComponent(matchId)}?compId=${encodeURIComponent(compId)}` : undefined;
  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-4 flex items-center gap-3">
        <Link
          to={backTo ?? '/matches'}
          className="inline-flex items-center gap-1 text-sm text-iron-400 hover:text-iron-200"
        >
          <ChevronLeft size={16} /> Back to match card
        </Link>
      </div>
      {children}
    </div>
  );
}

function EmptyState({ text, kind = 'info' }: { text: string; kind?: 'info' | 'error' }) {
  return (
    <div className="rounded-xl border border-iron-700 bg-iron-900 px-6 py-16 text-center">
      <p className={cn('font-mono text-sm', kind === 'error' ? 'text-red-400' : 'text-iron-400')}>{text}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function VetoPageBody({ data, me, matchId, compId, comp }: {
  data: MatchCardV2Response; me?: Me; matchId: string; compId: string; comp?: Competition;
}) {
  const { match } = data;
  const status: MatchCardStatus = (match.cardStatus ?? 'DRAFT') as MatchCardStatus;
  const gameModes = match.gameModes ?? [];

  // Same label helper CardBody uses for human-visible clan names — auto-fills
  // the header from the match, no separate lookup.
  const clan1Label = clanLabelFrom(match.clan1DisplayName, match.clan1);
  const clan2Label = clanLabelFrom(match.clan2DisplayName, match.clan2);

  // View-as-aware copy of `me` — BOTH the role gate and the IDENTITY gates
  // below read THIS, never the raw /me row. The role leg was already correct;
  // the identity leg was not, and it is the one that matters here: "which side
  // am I?" is resolved by comparing discordId to leader1Id/leader2Id, and
  // lib/viewAs.ts is explicit that the id IS a grant (see
  // VIEW_AS_SIM_DISCORD_ID, which names exactly this class of "is this row
  // mine?" check as the reason it is simulated away). Reading the raw id meant
  // a founder previewing a lower role kept banning and picking maps on a real,
  // live match as their own clan — a write the backend attributes to the real
  // founder, because the toggle is client-side and the token is unchanged.
  // Identity no-op whenever no view-as is active — see lib/viewAs.ts.
  const effectiveMe = useEffectiveMe(me);
  const { viewAs } = useViewAs();
  const isAdmin = effectiveMe?.role === 'founder' || effectiveMe?.role === 'admin';

  const isLeader1 = effectiveMe?.discordId === match.leader1Id;
  const isLeader2 = effectiveMe?.discordId === match.leader2Id;
  const isLeader = isLeader1 || isLeader2;
  // The CAPTAIN leg of "me" — the third grant this page reads, after the role
  // and the discordId. `viewerCaptainOf` is the REAL caller's captaincies,
  // computed server-side, and the server does not role-filter it under a
  // preview — so it still describes the founder, not the persona. Dropped
  // while previewing, exactly as PublicMatchCard.tsx already does for this
  // same field. Without this, a founder who genuinely captains some real team
  // kept ban/pick and faction writes on a LIVE match through every preview:
  // isSideRunner below is `isLeader || isCaptain1 || isCaptain2`, so the two
  // legs already simulated away were simply routed around by the third.
  // FAIL-CLOSED deliberately, same reasoning as PublicMatchCard's: a preview
  // may not claim authority over a specific real match row. Identity no-op
  // whenever no view-as is active, so a real captain loses nothing.
  const viewerCaptainOf = viewAs ? undefined : data.viewerCaptainOf;
  // Same captain-authority helper CardBody uses (mirrors the backend's
  // isActiveSideCaptain exactly) — a delegated team captain gets a say in
  // veto same as a leader does.
  const isCaptain1 = isMatchSideCaptain(viewerCaptainOf, match.clan1, match.clan1TeamNo);
  const isCaptain2 = isMatchSideCaptain(viewerCaptainOf, match.clan2, match.clan2TeamNo);
  const isSideRunner = isLeader || isCaptain1 || isCaptain2;

  // Which side the board lets you ban/pick FOR — same effective identity as
  // isLeader1/isLeader2 above, never the raw one.
  const myClanId = effectiveMe?.discordId === match.leader1Id
    ? match.clan1
    : effectiveMe?.discordId === match.leader2Id ? match.clan2 : undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 px-1 font-mono text-xs text-iron-500">
        <span className="inline-flex items-center gap-1"><Swords size={12} /> Match {String(matchId).replace(/^MATCH#/, '')}</span>
      </div>

      <h1 className="px-1 font-display text-2xl font-extrabold uppercase tracking-wider text-white">
        {clan1Label} <span className="text-iron-500">vs</span> {clan2Label}
      </h1>

      <VetoBoard
        veto={match.mapVeto}
        guide={data.vetoGuide}
        myClanId={myClanId}
        clan1Id={match.clan1}
        clan2Id={match.clan2}
        clan1Label={clan1Label}
        clan2Label={clan2Label}
        gameModes={gameModes}
        status={status}
        matchId={matchId}
        compId={compId}
        canAct={!!me && (isSideRunner || isAdmin)}
        isAdmin={isAdmin}
        compName={comp?.name}
        cardBranding={data.vetoCardBranding}
        showDownload
      />
    </div>
  );
}
